import { useState, useEffect } from "react";
import { ThumbsUp, ThumbsDown, Bookmark, ImageOff } from "lucide-react";
import LoadingSpinner from "./LoadingSpinner";
import { toggleThumb } from "../../api/feed";
import ShareArticleButton from "./ShareArticleButton";

const APP_BLUE = "#1c9dea";

const ActionPill = ({ active, label, onClick, children }) => {
  const handle = (e) => {
    e.stopPropagation();
    try {
      if (navigator.vibrate) navigator.vibrate(10);
    } catch {}
    onClick?.(e);
  };

  return (
    <button
      type="button"
      aria-pressed={!!active}
      aria-label={label}
      onClick={handle}
      className={[
        "inline-flex items-center justify-center rounded-full h-9 w-9 border backdrop-blur-sm transition-all duration-200",
        "active:scale-95 focus:outline-none focus:ring-2 focus:ring-[#1c9dea]/40",
        active
          ? "border-[#1c9dea]/50 bg-gray-900/90"
          : "border-[#0EA5E9]/25 bg-gray-900/80 hover:scale-105 hover:border-[#38BDF8]/40",
      ].join(" ")}
    >
      {children}
    </button>
  );
};

const ArticleCard = (props) => {
  const [isHovered, setIsHovered] = useState(false);
  const [localLike, setLocalLike] = useState(props.articleLike);
  const [localDislike, setLocalDislike] = useState(props.articleDislike);
  const [localCatalog, setLocalCatalog] = useState(props.articleCatalog);
  const [isBroken, setIsBroken] = useState(false);
  const [isImageLoaded, setIsImageLoaded] = useState(false);

  /* Sync local state when props change (e.g. from sidebar interaction) */
  useEffect(() => {
    setLocalLike(props.articleLike);
  }, [props.articleLike]);
  useEffect(() => {
    setLocalDislike(props.articleDislike);
  }, [props.articleDislike]);
  useEffect(() => {
    setLocalCatalog(props.articleCatalog);
  }, [props.articleCatalog]);

  const docId = props.article?.document_id;

  const handleLikeClick = (e) => {
    const next = !localLike;
    setLocalLike(next);
    toggleThumb({ document_id: docId, action: "like" });
    let newDislike = localDislike;
    if (localDislike) {
      setLocalDislike(false);
      toggleThumb({ document_id: docId, action: "dislike" });
      newDislike = false;
    }
    props.onInteract?.({
      like: next,
      dislike: newDislike,
      catalog: localCatalog,
    });
  };

  const handleDislikeClick = (e) => {
    const next = !localDislike;
    setLocalDislike(next);
    toggleThumb({ document_id: docId, action: "dislike" });
    let newLike = localLike;
    if (localLike) {
      setLocalLike(false);
      toggleThumb({ document_id: docId, action: "like" });
      newLike = false;
    }
    props.onInteract?.({ like: newLike, dislike: next, catalog: localCatalog });
  };

  const handleCatalogClick = (e) => {
    const next = !localCatalog;
    setLocalCatalog(next);
    toggleThumb({ document_id: docId, action: "catalog" });
    props.onInteract?.({
      like: localLike,
      dislike: localDislike,
      catalog: next,
    });
  };

  const imageClick = (article) => {
    props.investigateDoc(article.document_id);
  };

  const handleImageError = () => {
    setIsBroken(true);
    // Only signal truly broken (no useful data at all) to parent
    const hasContent =
      props.article?.title ||
      props.article?.content ||
      props.article?.target_type ||
      props.article?.source;
    if (!hasContent) props.onBroken?.();
  };

  const showActions = isHovered || localLike || localDislike || localCatalog;

  // Shared action buttons used in both broken and normal card states
  const actionButtons = showActions && (
    <div className="absolute bottom-3 left-0 right-0 flex items-center justify-center gap-3 px-3">
      <ActionPill
        active={localLike}
        label={localLike ? "Unlike" : "Like"}
        onClick={handleLikeClick}
      >
        <ThumbsUp
          className="h-5 w-5"
          strokeWidth={1.5}
          style={{
            color: localLike ? APP_BLUE : "rgb(203 213 225)",
            fill: localLike ? APP_BLUE : "none",
            filter: localLike
              ? "drop-shadow(0 0 6px rgba(28,157,234,0.6))"
              : "none",
          }}
        />
      </ActionPill>
      <ActionPill
        active={localDislike}
        label={localDislike ? "Remove dislike" : "Dislike"}
        onClick={handleDislikeClick}
      >
        <ThumbsDown
          className="h-5 w-5"
          strokeWidth={1.5}
          style={{
            color: localDislike ? APP_BLUE : "rgb(203 213 225)",
            fill: localDislike ? APP_BLUE : "none",
            filter: localDislike
              ? "drop-shadow(0 0 6px rgba(28,157,234,0.6))"
              : "none",
          }}
        />
      </ActionPill>
      <ActionPill
        active={localCatalog}
        label={localCatalog ? "Remove bookmark" : "Bookmark"}
        onClick={handleCatalogClick}
      >
        <Bookmark
          className="h-5 w-5"
          strokeWidth={1.5}
          style={{
            color: localCatalog ? APP_BLUE : "rgb(203 213 225)",
            fill: localCatalog ? APP_BLUE : "none",
            filter: localCatalog
              ? "drop-shadow(0 0 6px rgba(28,157,234,0.6))"
              : "none",
          }}
        />
      </ActionPill>
      <ShareArticleButton
        documentId={docId}
        article={props.article}
        variant="pill"
      />
    </div>
  );

  // Completely empty — no screenshot AND no content
  if (
    isBroken &&
    !props.article?.title &&
    !props.article?.content &&
    !props.article?.target_type &&
    !props.article?.source
  ) {
    return null;
  }

  // Broken screenshot but has content — show fallback card
  if (isBroken) {
    return (
      <div
        className={[
          "group relative h-96 w-full m-2 rounded-lg overflow-hidden cursor-pointer border backdrop-blur-sm transition-all duration-300",
          props.isActive
            ? "border-[#1c9dea] shadow-[0_0_12px_rgba(28,157,234,0.7),_0_0_30px_rgba(28,157,234,0.5),_0_0_60px_rgba(28,157,234,0.25)]"
            : "border-[#0EA5E9]/15 hover:border-[#0EA5E9]/30",
          props.isActive
            ? "bg-gray-900/50"
            : "bg-gray-900/40 hover:bg-gray-900/50",
        ].join(" ")}
        onMouseEnter={() => setIsHovered(true)}
        onMouseLeave={() => setIsHovered(false)}
        onClick={() => imageClick(props.article)}
      >
        <div className="relative h-full w-full flex flex-col">
          {/* Hover preview at top */}
          {props.hoverPreview && (
            <div className="relative z-20">{props.hoverPreview}</div>
          )}

          {/* Broken image placeholder */}
          <div className="flex-1 flex flex-col items-center justify-center gap-2 px-4">
            <ImageOff size={32} style={{ color: "rgba(148,163,184,0.25)" }} />
            <span style={{ color: "rgba(148,163,184,0.3)", fontSize: 11 }}>
              Screenshot unavailable
            </span>
          </div>

          {/* Type badge */}
          {!props.hoverPreview && props.typeBadge && (
            <div className="absolute top-2.5 left-2.5 z-10">
              {props.typeBadge}
            </div>
          )}
        </div>

        {actionButtons}
      </div>
    );
  }

  return (
    <div
      className={[
        "group relative h-96 w-full m-2 rounded-lg overflow-hidden cursor-pointer border backdrop-blur-sm transition-all duration-300",
        props.isActive
          ? "border-[#1c9dea] shadow-[0_0_12px_rgba(28,157,234,0.7),_0_0_30px_rgba(28,157,234,0.5),_0_0_60px_rgba(28,157,234,0.25)]"
          : "border-[#0EA5E9]/15 hover:border-[#0EA5E9]/30",
        props.isActive
          ? "bg-gray-900/50"
          : "bg-gray-900/40 hover:bg-gray-900/50",
      ].join(" ")}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      onClick={() => imageClick(props.article)}
    >
      <div className="relative h-full w-full">
        {!isImageLoaded && (
          <div className="absolute inset-0 flex items-center justify-center">
            <LoadingSpinner />
          </div>
        )}

        <img
          className={[
            "absolute inset-0 h-full w-full object-cover object-top transition-opacity duration-300",
            isImageLoaded ? "opacity-100" : "opacity-0",
          ].join(" ")}
          src={props.article?.screenshot}
          alt={props.article?.title || "Article screenshot"}
          onLoad={() => setIsImageLoaded(true)}
          onError={handleImageError}
          loading="lazy"
          draggable={false}
        />

        <div className="absolute bottom-0 left-0 right-0 h-36 bg-gradient-to-t from-black/70 via-black/40 to-transparent" />

        {/* Type badge */}
        {props.typeBadge && (
          <div className="absolute top-2.5 left-2.5 z-10">
            {props.typeBadge}
          </div>
        )}

        {/* Hover preview overlay */}
        {props.hoverPreview && (
          <div className="absolute top-0 left-0 right-0 z-20">
            {props.hoverPreview}
          </div>
        )}
      </div>

      {actionButtons}
    </div>
  );
};

export default ArticleCard;
